<template>
  <div v-if="prospects?.prospects" data-testid="prospects-catalog">
    <div class="mt-4 flex items-start gap-2">
      <ComboSearch
        :model-value="filters.companyNames.value"
        class="shrink-0 grow basis-0"
        :label="t('ProspectsCatalog.searchCompanyLabel')"
        :placeholder="t('ProspectsCatalog.companyNameSearch')"
        data-testid="prospects-search-company"
        @update:model-value="updateFilters('companyNames', $event)"
      />
      <ComboSearch
        :model-value="filters.positions.value"
        class="shrink-0 grow basis-0"
        :label="t('ProspectsCatalog.searchPositionLabel')"
        :placeholder="t('ProspectsCatalog.positionSearch')"
        data-testid="prospects-search-position"
        @update:model-value="updateFilters('positions', $event)"
      />
    </div>

    <div class="mt-4 grid grid-cols-4 gap-2">
      <div v-for="field in filterFields" :key="field.filterKey">
        <label class="mb-0.5 block text-body-s-bold">{{ t(field.labelKey) }}</label>
        <RichSelectField
          :id="`prospects-catalog-${field.filterKey}`"
          :model-value="field.modelValue"
          class="w-full"
          counter
          multiple
          :placeholder="t('VisitorProfiles.filterPanel.select')"
          searchable
          @update:model-value="updateFilters(field.filterKey, $event as string[])"
        >
          <RichSelectOption v-for="option in field.options" :key="option.value" :value="option.value" :label="option.text" />
        </RichSelectField>
      </div>
    </div>
    <button class="mt-2" @click.stop="emit('resetFilters')">
      {{ t('ProspectsCatalog.resetFilters') }}
    </button>

    <MessageBanner
      v-if="leadAddConfig.visible"
      class="my-4"
      :headline="leadAddConfig.title"
      :message="leadAddConfig.copy"
      :type="leadAddConfig.type"
      data-testid="prospects-lead-limit-banner"
    />

    <TableLight v-if="prospects.prospectsCount.total" class="mt-6 mb-6 w-full" data-testid="prospects-table">
      <thead>
        <tr>
          <th class="text-start">{{ t('ProspectsCatalog.companyName') }}</th>
          <th class="text-start">{{ t('ProspectsCatalog.position') }}</th>
          <th class="text-start">{{ t('ProspectsCatalog.visitedEvents') }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <ProspectRow
          v-for="prospect in prospects.prospects"
          :key="prospect.id"
          :facet-items="facetItems"
          :prospect-list-id="getProspectListId(prospect.id)"
          :prospect-lists="props.prospectLists"
          :prospect="prospect"
          :disabled="leadAddConfig.addDisabled"
          @create-prospect-item="emit('createProspectItem', $event)"
          @delete-prospect-item="emit('deleteProspectItem', $event)"
        >
          <button
            v-if="getProspectListId(prospect.id)"
            class="h-6 cursor-default"
            :data-testid="`prospects-already-added-${prospect.id}`"
            :title="`${t('ProspectsCatalog.prospectAlreadyAdded')}`"
          >
            <SvgIcon :path="mdiCheck" :size="20" />
          </button>

          <DropDownSelect
            v-else-if="prospect.available && !leadAddConfig.addDisabled"
            inner-class="min-w-[150px]"
            direction="right"
            :data-testid="`prospects-add-to-list-${prospect.id}`"
          >
            <template #activator="{ open }">
              <button class="h-6" data-testid="prospects-add-trigger" @click.stop="open">
                <SvgIcon :path="mdiPlaylistPlus" :size="20" />
              </button>
            </template>

            <template #default="{ close }">
              <ul class="m-0 list-none p-0" data-testid="prospects-add-options">
                <template v-if="prospectLists.length">
                  <li v-for="list in props.prospectLists" :key="`list_${list.id}`" class="separate-rows">
                    <label class="block" :class="isListChangeable(list) ? 'cursor-pointer' : 'text-subtlest'">
                      <div
                        class="px-3 py-2 peer-checked:bg-primary-subtler hover:bg-primary-subtler"
                        @click.stop="isListChangeable(list) && addProspectToList(prospect.id, list.id, close)"
                      >
                        {{ list.title }}
                      </div>
                    </label>
                  </li>
                </template>
                <template v-else>
                  <li class="separate-rows">
                    <label class="block cursor-pointer">
                      <div class="px-3 py-2 peer-checked:bg-primary-subtler hover:bg-primary-subtler">
                        {{ $t('ProspectsCatalog.addToListNoListFound') }}
                      </div>
                    </label>
                  </li>
                </template>
              </ul>
            </template>
          </DropDownSelect>

          <button
            v-else-if="!leadAddConfig.addDisabled"
            v-tooltip="t('ProspectsCatalog.unavailableTooltip')"
            disabled
            class="h-6 text-subtlest"
          >
            <SvgIcon :path="mdiPlaylistPlus" :size="20" />
          </button>
        </ProspectRow>
      </tbody>
    </TableLight>

    <div v-else class="py-10 text-center" data-testid="prospects-no-results">
      {{ t('ProspectsCatalog.noResults') }}
    </div>

    <div v-if="!(!hasNextPage && props.page <= 1)" class="mx-auto mt-6 mb-4 flex" data-testid="prospects-pagination">
      <button
        :disabled="props.page <= 1"
        class="btn-secondary-neutral-s mr-3 mb-16"
        data-testid="prospects-previous-page"
        @click="goToPreviousPage"
      >
        <SvgIcon :path="mdiChevronLeft" :size="20" />
        {{ t('ProspectsCatalog.previous') }}
      </button>
      <button :disabled="!hasNextPage" class="btn-secondary-neutral-s mr-3 mb-16" data-testid="prospects-next-page" @click="goToNextPage">
        {{ t('ProspectsCatalog.next') }}
        <SvgIcon :path="mdiChevronRight" :size="20" />
      </button>
    </div>
    <div class="mx-auto mt-4 mb-4" data-testid="prospects-pagination-hint">
      {{ paginationHint }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiCheck, mdiPlaylistPlus, mdiChevronLeft, mdiChevronRight } from '@mdi/js'
import { MessageBanner, RichSelectField, RichSelectOption } from '@ramp106/omrjs-core-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import type { ProspectFacetItems } from '@/components/Prospects/prospectFacetItems'
import type { ProspectsQuery } from '@/gql/myomr'
import { useMeStore } from '@/stores/me'
import { TableLight } from '@/ui/TableLight'

import { isListChangeable } from './helpers'
import ProspectRow from './ProspectRow.vue'
import type { FilterName, Filters, ProspectSearchItemWithListId, ProspectList } from './types'

const { t } = useI18n()
const { leadGenLimit } = useMeStore()
const route = useRoute()

const props = defineProps<{
  facetItems: ProspectFacetItems
  filters: Filters
  page: number
  perPage: number
  prospectListItems: ProspectSearchItemWithListId[]
  prospectLists: ProspectList[]
  prospects: ProspectsQuery['prospects']
}>()

const emit = defineEmits<{
  (e: 'createProspectItem', value: { prospectId: string; prospectListId: string }): void
  (e: 'deleteProspectItem', value: { prospectId: string; prospectListId: string }): void
  (e: 'resetFilters'): void
  (e: 'updateFilters', value: { filterName: string; values: string[] }): void
  (e: 'update:page', value: number): void
  (e: 'update:perPage', value: number): void
}>()

const availableFilterOptions = [
  { text: t('ProspectsCatalog.availabilityFilter.available'), value: 'available' },
  { text: t('ProspectsCatalog.availabilityFilter.unavailable'), value: 'unavailable' },
]

type FilterField = {
  filterKey: FilterName
  labelKey: string
  modelValue: string[]
  options: { text: string; value: string }[]
}

function updateFilters(filterName: FilterName, values: string[]) {
  emit('updateFilters', { filterName: filterName, values: values })
}

const paginationHint = computed(() => {
  return props.prospects?.prospectsCount.relation === 'gte' ? t('ProspectsCatalog.paginationHint') : ''
})

const maxLeadNumber = computed(() => {
  return leadGenLimit(route.params.slug as string)
})

const currLeadNumber = computed(() => {
  return props.prospectLists.map((pl) => pl.prospects.length).reduce((sum, l) => sum + l, 0)
})

const enrichLeadMessage = (msg: string) => {
  return maxLeadNumber.value
    ? msg
        .replace('<<remainingleads>>', Math.max(maxLeadNumber.value - currLeadNumber.value, 0).toString())
        .replace('<<maxleads>>', maxLeadNumber.value.toString())
    : ''
}

const leadAddConfig = computed(() => {
  const type = (maxLeadNumber.value && currLeadNumber.value >= maxLeadNumber.value ? 'error' : 'success') as
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
  const addDisabled = !!maxLeadNumber.value && type == 'error'
  return {
    addDisabled,
    visible: !!maxLeadNumber.value,
    title: !addDisabled
      ? enrichLeadMessage(t('ProspectsCatalog.leadRemainingTitle'))
      : enrichLeadMessage(t('ProspectsCatalog.noMoreLeadsTitle')),
    copy: !addDisabled ? '' : enrichLeadMessage(t('ProspectsCatalog.noMoreLeadsMessage')),
    type,
  }
})

function addProspectToList(prospectId: string, prospectListId: string, callback: () => void) {
  emit('createProspectItem', { prospectId: prospectId, prospectListId: prospectListId })
  callback()
}

function getProspectListId(prospectId: string) {
  return props.prospectListItems.find((item) => item.id === prospectId)?.prospectListId
}

const hasNextPage = computed(() => {
  return Math.ceil((props?.prospects?.prospectsCount.total || 0) / props.perPage) > props.page
})

function goToNextPage() {
  if (hasNextPage.value) {
    emit('update:page', props.page + 1)
  }
}

function goToPreviousPage() {
  if (props.page > 1) {
    emit('update:page', props.page - 1)
  }
}

const filterFields = computed(() => {
  const fields: FilterField[] = [
    {
      filterKey: 'visitedEvents',
      labelKey: 'ProspectsCatalog.visitedEvents',
      modelValue: props.filters.visitedEvents.value,
      options: props.facetItems.visitedEventsItems,
    },
    {
      filterKey: 'companyJobLevels',
      labelKey: 'ProspectsCatalog.companyJobLevel',
      modelValue: props.filters.companyJobLevels.value,
      options: props.facetItems.companyJobLevelItems,
    },
    {
      filterKey: 'companySizes',
      labelKey: 'ProspectsCatalog.companySize',
      modelValue: props.filters.companySizes.value,
      options: props.facetItems.companySizeItems,
    },
    {
      filterKey: 'companyIndustries',
      labelKey: 'ProspectsCatalog.companyIndustry',
      modelValue: props.filters.companyIndustries.value,
      options: props.facetItems.companyIndustryItems,
    },
    {
      filterKey: 'companyFields',
      labelKey: 'ProspectsCatalog.companyField',
      modelValue: props.filters.companyFields.value,
      options: props.facetItems.companyFieldItems,
    },
    {
      filterKey: 'companyDepartments',
      labelKey: 'ProspectsCatalog.companyDepartment',
      modelValue: props.filters.companyDepartments.value,
      options: props.facetItems.companyDepartmentItems,
    },
    {
      filterKey: 'available',
      labelKey: 'ProspectsCatalog.availabilityFilter.title',
      modelValue: props.filters.available.value,
      options: availableFilterOptions,
    },
    {
      filterKey: 'companyCountries',
      labelKey: 'ProspectsCatalog.companyCountry',
      modelValue: props.filters.companyCountries.value,
      options: props.facetItems.companyCountryItems,
    },
  ]

  return fields.filter((field) => field.options.length > 0)
})
</script>
